import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import MarketDetailsForm from './components/MarketDetailsForm';
import MarketSettingsForm from './components/MarketSettingsForm';
import MarketReview from './components/MarketReview';
import MarketPreview from './components/MarketPreview';
import MarketGuidelines from './components/MarketGuidelines';
import { createMarket, saveDraftMarket } from '../../utils/api';

const MarketCreation = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  const [marketData, setMarketData] = useState({
    title: '',
    description: '',
    category: '',
    tags: [],
    endDate: '',
    endTime: '',
    timezone: 'UTC',
    outcomeType: 'binary',
    outcomes: ['Yes', 'No'],
    customOutcomes: [],
    minBetAmount: 1,
    maxBetAmount: 10000,
    creatorFee: 2,
    resolutionSource: '',
    resolutionCriteria: '',
    isPublic: true,
    allowEarlyResolution: false,
    status: 'draft'
  });

  const steps = [
    { id: 1, title: 'Market Details', icon: 'FileText', completed: false },
    { id: 2, title: 'Settings', icon: 'Settings', completed: false },
    { id: 3, title: 'Review', icon: 'Eye', completed: false },
    { id: 4, title: 'Publish', icon: 'Send', completed: false }
  ];

  const categories = [
    { id: 'Politics', label: 'Politics', icon: 'Vote' },
    { id: 'Cryptocurrency', label: 'Cryptocurrency', icon: 'Bitcoin' },
    { id: 'Stocks', label: 'Stocks', icon: 'TrendingUp' },
    { id: 'Technology', label: 'Technology', icon: 'Cpu' },
    { id: 'Sports', label: 'Sports', icon: 'Trophy' },
    { id: 'Climate', label: 'Climate', icon: 'Globe' },
    { id: 'Space', label: 'Space', icon: 'Rocket' },
    { id: 'Entertainment', label: 'Entertainment', icon: 'Film' },
    { id: 'Economics', label: 'Economics', icon: 'DollarSign' },
    { id: 'Other', label: 'Other', icon: 'MoreHorizontal' }
  ];

  // Auto-save draft functionality
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (marketData?.title?.trim() && !isLoading) {
        handleSaveDraft(true); // Silent save
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [marketData, isLoading]);

  const updateMarketData = (field, value) => {
    setMarketData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error when field is updated
    if (validationErrors?.[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateStep = (step) => {
    const errors = {};

    switch (step) {
      case 1:
        if (!marketData?.title?.trim()) {
          errors.title = 'Market question is required';
        } else if (marketData?.title?.length < 10) {
          errors.title = 'Market question must be at least 10 characters';
        } else if (marketData?.title?.length > 200) {
          errors.title = 'Market question must be less than 200 characters';
        }

        if (!marketData?.description?.trim()) {
          errors.description = 'Description is required';
        } else if (marketData?.description?.length < 50) {
          errors.description = 'Description must be at least 50 characters';
        }

        if (!marketData?.category) {
          errors.category = 'Category is required';
        }

        if (!marketData?.endDate) {
          errors.endDate = 'End date is required';
        } else {
          const endDateTime = new Date(`${marketData?.endDate}T${marketData?.endTime || '23:59'}`);
          const now = new Date();
          if (endDateTime <= now) {
            errors.endDate = 'End date must be in the future';
          }
        }
        break;

      case 2:
        if (marketData?.minBetAmount < 0.01) {
          errors.minBetAmount = 'Minimum bet must be at least $0.01';
        }
        if (marketData?.maxBetAmount <= marketData?.minBetAmount) {
          errors.maxBetAmount = 'Maximum bet must be greater than minimum bet';
        }
        if (marketData?.creatorFee < 0 || marketData?.creatorFee > 10) {
          errors.creatorFee = 'Creator fee must be between 0% and 10%';
        }
        if (!marketData?.resolutionCriteria?.trim()) {
          errors.resolutionCriteria = 'Resolution criteria is required';
        }
        break;

      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSaveDraft = async (silent = false) => {
    if (!silent) setIsSavingDraft(true);
    
    try {
      const response = await saveDraftMarket(marketData);
      if (response?.success && !silent) {
        // Show success message
        console.log('Draft saved successfully');
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      if (!silent) setIsSavingDraft(false);
    }
  };

  const handlePublishMarket = async () => {
    if (!validateStep(1) || !validateStep(2)) {
      return;
    }

    setIsLoading(true);
    
    try {
      const finalMarketData = {
        ...marketData,
        status: 'active',
        createdAt: new Date()?.toISOString()
      };

      const response = await createMarket(finalMarketData);
      
      if (response?.success) {
        navigate(`/market/${response?.data?.id}`, {
          state: { message: 'Market created successfully!' }
        });
      } else {
        throw new Error(response?.error || 'Failed to create market');
      }
    } catch (error) {
      console.error('Failed to create market:', error);
      setValidationErrors({
        publish: error?.message || 'Failed to create market. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <MarketDetailsForm
            marketData={marketData}
            updateMarketData={updateMarketData}
            categories={categories}
            validationErrors={validationErrors}
          />
        );
      case 2:
        return (
          <MarketSettingsForm
            marketData={marketData}
            updateMarketData={updateMarketData}
            validationErrors={validationErrors}
          />
        );
      case 3:
        return (
          <MarketReview
            marketData={marketData}
            categories={categories}
            onEdit={(step) => setCurrentStep(step)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="px-6 py-6">
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <div className="flex items-center space-x-2 text-sm">
            <button
              onClick={() => navigate('/markets')}
              className="text-text-secondary hover:text-text-primary transition-colors duration-trading-fast"
            >
              Markets
            </button>
            <Icon name="ChevronRight" size={16} className="text-text-secondary" />
            <span className="text-text-primary font-body-medium">Create Market</span>
          </div>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-heading-bold text-text-primary mb-4">
            Create Prediction Market
          </h1>
          <p className="text-lg text-text-secondary max-w-3xl">
            Design and publish your own prediction market. Set the question, define outcomes, and let others trade on future events.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Progress Steps */}
          <div className="lg:col-span-1">
            <div className="bg-surface border border-border rounded-trading shadow-trading-card p-6 sticky top-24">
              <h3 className="font-body-semibold text-text-primary mb-4">Progress</h3>
              <div className="space-y-4">
                {steps?.map((step, index) => (
                  <div
                    key={step?.id}
                    className={`flex items-center space-x-3 p-3 rounded-trading transition-all duration-trading-fast ${
                      currentStep === step?.id
                        ? 'bg-primary-900 border border-primary-800 text-primary-300'
                        : currentStep > step?.id
                        ? 'bg-success-900 bg-opacity-20 border border-success-800 text-success' :'text-text-secondary hover:bg-surface-hover'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      currentStep === step?.id
                        ? 'bg-primary text-white'
                        : currentStep > step?.id
                        ? 'bg-success text-white' :'bg-surface-hover'
                    }`}>
                      {currentStep > step?.id ? (
                        <Icon name="Check" size={16} />
                      ) : (
                        <Icon name={step?.icon} size={16} />
                      )}
                    </div>
                    <div>
                      <p className="font-body-medium text-sm">{step?.title}</p>
                      <p className="text-xs opacity-70">Step {step?.id}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Save Draft Button */}
              <button
                onClick={() => handleSaveDraft()}
                disabled={isSavingDraft || !marketData?.title?.trim()}
                className="w-full mt-6 bg-surface-hover text-text-primary px-4 py-2 rounded-trading font-body-medium text-sm border border-border hover:bg-border-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-trading-fast"
              >
                {isSavingDraft ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-text-primary border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Save" size={16} />
                    <span>Save Draft</span>
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-surface border border-border rounded-trading shadow-trading-card p-8">
              {renderStepContent()}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                <button
                  onClick={handlePrevStep}
                  disabled={currentStep === 1}
                  className="flex items-center space-x-2 px-6 py-2 bg-surface-hover text-text-primary rounded-trading font-body-medium border border-border hover:bg-border-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-trading-fast"
                >
                  <Icon name="ChevronLeft" size={16} />
                  <span>Previous</span>
                </button>

                {currentStep < 3 ? (
                  <button
                    onClick={handleNextStep}
                    className="flex items-center space-x-2 px-6 py-2 bg-gradient-primary text-white rounded-trading font-body-medium hover:opacity-90 transition-opacity duration-trading-fast glow-primary"
                  >
                    <span>Next</span>
                    <Icon name="ChevronRight" size={16} />
                  </button>
                ) : (
                  <button
                    onClick={handlePublishMarket}
                    disabled={isLoading}
                    className="flex items-center space-x-2 px-6 py-2 bg-gradient-success text-white rounded-trading font-body-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-trading-fast glow-success"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Publishing...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Send" size={16} />
                        <span>Publish Market</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {validationErrors?.publish && (
                <div className="mt-4 p-4 bg-error-900 bg-opacity-20 border border-error-800 rounded-trading">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={16} className="text-error" />
                    <span className="text-error text-sm">{validationErrors?.publish}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Live Preview */}
            <MarketPreview marketData={marketData} categories={categories} />
            
            {/* Guidelines */}
            <MarketGuidelines />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketCreation;